import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { BarChart3, RefreshCw, MessageSquare, Camera, LayoutDashboard, Facebook } from 'lucide-react';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { useTheme } from '../../contexts/ThemeContext';
import { analyticsAPI } from '../../services/api';
import ResumoGeral from './components/ResumoGeral';
import GraficoTemporal from './components/GraficoTemporal';
import GraficoSentimento from './components/GraficoSentimento';
import TabelaCategorias from './components/TabelaCategorias';
import NuvemPalavras from './components/NuvemPalavras';
import HorariosPico from './components/HorariosPico';
import RankingContatos from './components/RankingContatos';
import TempoRespostaInstancias from './components/TempoRespostaInstancias';
import FiltrosPeriodo from './components/FiltrosPeriodo';
import InstagramInsights from './components/InstagramInsights';
import FacebookInsights from './components/FacebookInsights';
import TopCommentersConsolidated from './components/TopCommentersConsolidated';

const Analytics = () => {
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [loadingPeriodo, setLoadingPeriodo] = useState(false);
  const [resumo, setResumo] = useState(null);
  const [metricas, setMetricas] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState('geral');
  const [filtros, setFiltros] = useState({
    dataInicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dataFim: new Date().toISOString().split('T')[0],
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clienteId = user.cliente_id;

  useEffect(() => {
    if (clienteId) { loadResumo(); loadMetricasPeriodo(); }
  }, [clienteId]);

  useEffect(() => {
    if (clienteId && filtros.dataInicio && filtros.dataFim) loadMetricasPeriodo();
  }, [filtros]);

  const loadResumo = async () => {
    try {
      setLoading(true);
      const dias = Math.ceil((new Date(filtros.dataFim) - new Date(filtros.dataInicio)) / (1000 * 60 * 60 * 24));
      const response = await analyticsAPI.getResumo(clienteId, dias);
      setResumo(response.data);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMetricasPeriodo = async () => {
    try {
      setLoadingPeriodo(true);
      const response = await analyticsAPI.getPeriodo(clienteId, filtros);
      setMetricas(response.data);
    } catch (error) {
      console.error('Erro ao carregar métricas do período:', error);
    } finally {
      setLoadingPeriodo(false);
    }
  };

  const handleRefresh = () => {
    loadResumo();
    loadMetricasPeriodo();
    setRefreshTrigger(prev => prev + 1);
    toast.info('Atualizando métricas...');
  };

  const tabs = [
    { id: 'geral', label: 'Geral', icon: LayoutDashboard },
    { id: 'mensagens', label: 'Mensagens', icon: MessageSquare },
    { id: 'instagram', label: 'Instagram', icon: Camera },
    { id: 'facebook', label: 'Facebook', icon: Facebook },
  ];

  const sectionTitle = (icon, title) => {
    const Icon = icon;
    return (
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
          <Icon className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
        </div>
        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-6">
        <PageHeader title="Analytics" subtitle="Métricas e análise de desempenho">
          <button
            onClick={handleRefresh}
            disabled={loading || loadingPeriodo}
            className="flex items-center space-x-1.5 sm:space-x-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading || loadingPeriodo ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
        </PageHeader>

        {/* Filtros */}
        <FiltrosPeriodo filtros={filtros} onChange={setFiltros} />

        {/* Tabs */}
        <div className={`flex gap-1 p-1 rounded-xl ${darkMode ? 'bg-zinc-800' : 'bg-gray-100'}`}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-2 sm:py-2.5 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  isActive
                    ? darkMode ? 'bg-zinc-700 text-white shadow' : 'bg-white text-gray-900 shadow'
                    : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* === ABA GERAL === */}
        {activeTab === 'geral' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Resumo de Mensagens */}
            <ResumoGeral resumo={resumo} loading={loading} />

            {/* Top Comentadores Consolidado */}
            <TopCommentersConsolidated clienteId={clienteId} refreshTrigger={refreshTrigger} />

            {/* Instagram Perfis (colapsável) */}
            <InstagramInsights clienteId={clienteId} instanciaId={filtros.instanciaId} filtros={filtros} refreshTrigger={refreshTrigger} />

            {/* Facebook Perfis (colapsável) */}
            <FacebookInsights clienteId={clienteId} filtros={filtros} refreshTrigger={refreshTrigger} />

            {/* Gráficos */}
            <GraficoTemporal metricas={metricas} loading={loadingPeriodo} />
            <GraficoSentimento metricas={metricas} loading={loadingPeriodo} filtros={filtros} />

            {/* Operacional */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <HorariosPico filtros={filtros} />
              <TempoRespostaInstancias filtros={filtros} />
            </div>

            {/* Conteúdo */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <NuvemPalavras filtros={filtros} />
              <RankingContatos filtros={filtros} />
            </div>
          </div>
        )}

        {/* === ABA MENSAGENS === */}
        {activeTab === 'mensagens' && (
          <div className="space-y-4 sm:space-y-6">
            <ResumoGeral resumo={resumo} loading={loading} />
            <GraficoTemporal metricas={metricas} loading={loadingPeriodo} />
            <GraficoSentimento metricas={metricas} loading={loadingPeriodo} filtros={filtros} />
            <TabelaCategorias metricas={metricas} loading={loadingPeriodo} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <HorariosPico filtros={filtros} />
              <TempoRespostaInstancias filtros={filtros} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <NuvemPalavras filtros={filtros} />
              <RankingContatos filtros={filtros} />
            </div>
          </div>
        )}

        {/* === ABA INSTAGRAM === */}
        {activeTab === 'instagram' && (
          <div className="space-y-4 sm:space-y-6">
            <InstagramInsights clienteId={clienteId} instanciaId={filtros.instanciaId} filtros={filtros} refreshTrigger={refreshTrigger} />
          </div>
        )}

        {/* === ABA FACEBOOK === */}
        {activeTab === 'facebook' && (
          <div className="space-y-4 sm:space-y-6">
            <FacebookInsights clienteId={clienteId} filtros={filtros} refreshTrigger={refreshTrigger} />
          </div>
        )}

        {/* Info do período */}
        {resumo?.periodo && (
          <div className={`p-4 rounded-lg border text-center ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Período: <span className="font-semibold">
                {new Date(resumo.periodo.inicio).toLocaleDateString('pt-BR')} — {new Date(resumo.periodo.fim).toLocaleDateString('pt-BR')}
              </span> ({resumo.periodo.dias} dias)
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;
